<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('SubNodeTypes.subNodeContainerType') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <v-skeleton-loader
        v-if="isFetching"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <TableRemote
        v-if="hasPermission('nodes.types.view')"
        :iconHeader="hasPermission('nodes.types.add') ? 'add' : null"
        :items="displayedcontainerTypes"
        isSearchable
        :headers="tableHeaders"
        :itemActions="actions"
        isCustomIcon
        :title="$t('SubNodeTypes.subNodeContainerType')"
        :loading="isFetching"
        :pagination="pagination"
        :centerColumns="['action']"
        :notSortableColumns="['action']"
        :sortableColumns="['name']"
        hideDefaultFooter
        @iconAction="onClickAction"
        @sendQueryParams="fetchQueryParams"
      >
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </TableRemote>
      <NoPermissionAlert
        v-else
        :message="$t('permissions.error')"
      />
    </template>
  </BoxWithTitle>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import ContainerForm from './ContainerForm.vue';

export default {
    components: {
        BoxWithTitle,
        TableRemote,
        NoPermissionAlert,
    },
    data() {
        return {
            containerTypes: [],
            pagination: {},
            isFetching: false,
            queryParams: { perpage: 25, page: 1 },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        displayedcontainerTypes() {
            return this.containerTypes.map(containerType => ({
                id: containerType.id,
                // eslint-disable-next-line vue/max-attributes-per-line
                name: !containerType.name[this.userLang] || !containerType.name[this.userLang].length
                    ? containerType.name[this.teamMainLang]
                    : containerType.name[this.userLang],
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('nodes.types.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('nodes.types.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        await this.getContainerTypes();
    },
    methods: {
        fetchQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
            this.getContainerTypes();
        },
        async getContainerTypes(nextPage = 1) {
            if (this.isFetching) {
                return;
            }
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getContainerTypes(this.queryParams);
                this.containerTypes = [...data.containerTypes];
                this.pagination = data.pagination;
                this.isFetching = false;
            }
            if (nextPage <= this.queryParams.page) {
                return;
            }

            this.isFetching = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getContainerTypes(this.queryParams);
            this.containerTypes = [...this.containerTypes, ...data.containerTypes];
            this.pagination = data.pagination;
            this.isFetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('SubNodeTypes.addContainerType'),
                component: Vue.component('ContainerForm', ContainerForm),
                showLangs: true,
                isForm: true,
                onConfirm: requestPayload => this.addContainer(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (!action && item === 'add') {
                this.onClickAdd();
                return;
            }

            if (action.mode === 'edit') {
                const { data } = await this.$rq.getContainerType(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('SubNodeTypes.updateContainerType'),
                    component: Vue.component('ContainerForm', ContainerForm),
                    showLangs: true,
                    isForm: true,
                    payload: { containerType: data, isEdition: true },
                    onConfirm: requestPayload => this.updateContainer(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('SubNodeTypes.removeContainerType'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    isForm: false,
                    payload: { msg: this.$t('SubNodeTypes.removeContainerType') },
                    onConfirm: () => this.removeContainer(item.id),
                });
            }
        },
        async addContainer(requestPayload) {
            const payload = { name: requestPayload };
            await this.$rq.addContainerType(payload);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.added'),
            });
            await this.getContainerTypes();
        },
        async updateContainer(id, requestPayload) {
            const payload = { name: requestPayload };
            await this.$rq.updateContainer(id, payload);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.updated'),
            });
            await this.getContainerTypes();
        },
        async removeContainer(id) {
            await this.$rq.removeContainer(id);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.removed'),
            });
            await this.getContainerTypes();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getContainerTypes(this.queryParams.page + 1);
                // access "v-autocomplete" ref
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
